import { useQuery } from "@tanstack/react-query";
import { useParams } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/lib/picker-search";
import { Sparkles, Filter } from "lucide-react";
import { useState } from "react";
import type { CreativeOutput, Campaign } from "@shared/schema";

const outputTypeLabels: Record<string, string> = {
  hook_bank: "Hook Banks",
  script: "Scripts",
  creator_brief: "Creator Briefs",
  copy: "Copy Variations",
  pdp_angle: "PDP Angles",
  email_sms: "Email/SMS",
  summary: "Summaries",
  other: "Other",
};

const funnelLayerLabels: Record<string, string> = {
  top: "Top of Funnel",
  mid: "Mid Funnel",
  bottom: "Bottom of Funnel",
};

export default function CreativeLabPage() {
  const { brandId } = useParams<{ brandId: string }>();
  const [selectedOutput, setSelectedOutput] = useState<CreativeOutput | null>(null);
  const [filterCampaign, setFilterCampaign] = useState<string>("all");
  const [filterFunnel, setFilterFunnel] = useState<string>("all");
  const [filterStage, setFilterStage] = useState<string>("all");
  const [filterAssetType, setFilterAssetType] = useState<string>("all");

  const { data: outputs, isLoading } = useQuery<CreativeOutput[]>({
    queryKey: [`/api/brands/${brandId}/outputs`],
    enabled: !!brandId,
  });

  const { data: campaigns } = useQuery<Campaign[]>({
    queryKey: [`/api/brands/${brandId}/campaigns`],
    enabled: !!brandId,
  });

  const campaignMap = new Map(campaigns?.map(c => [c.id, c]) || []);

  const filteredOutputs = outputs?.filter(o => {
    if (filterCampaign !== "all" && o.campaignId !== filterCampaign) return false;
    if (filterFunnel !== "all" && o.funnelLayer !== filterFunnel) return false;
    if (filterStage !== "all" && o.persuasionStage !== filterStage) return false;
    if (filterAssetType !== "all" && o.assetType !== filterAssetType) return false;
    return true;
  }) || [];

  const assetTypes = Array.from(new Set(outputs?.map(o => o.assetType).filter(Boolean) || []));

  const outputTypes = Object.keys(outputTypeLabels);
  const groupedOutputs = outputTypes.reduce((acc, type) => {
    acc[type] = filteredOutputs.filter(o => o.outputType === type);
    return acc;
  }, {} as Record<string, CreativeOutput[]>);

  const activeTypes = outputTypes.filter(t => groupedOutputs[t].length > 0);

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-6">
      <div className="fade-in-up">
        <h1 className="text-2xl font-bold tracking-tight" data-testid="text-creative-lab-title">Creative Lab</h1>
        <p className="text-sm text-muted-foreground mt-0.5">Generated creative outputs filtered by campaign context</p>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <SearchableSelect
          value={filterCampaign}
          onValueChange={setFilterCampaign}
          placeholder="Campaign"
          testId="select-filter-campaign"
          triggerClassName="w-[180px]"
          triggerIcon={<Filter className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />}
          options={[
            { value: "all", label: "All Campaigns" },
            ...(campaigns ?? []).map(c => ({ value: c.id, label: c.name })),
          ]}
        />
        <Select value={filterFunnel} onValueChange={setFilterFunnel}>
          <SelectTrigger className="w-[160px]" data-testid="select-filter-funnel">
            <SelectValue placeholder="Funnel Layer" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Funnels</SelectItem>
            <SelectItem value="top">Top of Funnel</SelectItem>
            <SelectItem value="mid">Mid Funnel</SelectItem>
            <SelectItem value="bottom">Bottom of Funnel</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterStage} onValueChange={setFilterStage}>
          <SelectTrigger className="w-[160px]" data-testid="select-filter-stage">
            <SelectValue placeholder="Persuasion Stage" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stages</SelectItem>
            <SelectItem value="awareness">Awareness</SelectItem>
            <SelectItem value="knowledge">Knowledge</SelectItem>
            <SelectItem value="liking">Liking</SelectItem>
            <SelectItem value="preference">Preference</SelectItem>
            <SelectItem value="conviction">Conviction</SelectItem>
            <SelectItem value="purchase">Purchase</SelectItem>
          </SelectContent>
        </Select>
        {assetTypes.length > 0 && (
          <Select value={filterAssetType} onValueChange={setFilterAssetType}>
            <SelectTrigger className="w-[160px]" data-testid="select-filter-asset-type">
              <SelectValue placeholder="Asset Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Asset Types</SelectItem>
              {assetTypes.map(t => <SelectItem key={t!} value={t!}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-3">{[1, 2, 3].map(i => <Skeleton key={i} className="h-24" />)}</div>
      ) : filteredOutputs.length > 0 ? (
        <div className="space-y-6">
          {activeTypes.map(type => (
            <div key={type} className="space-y-3">
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">{outputTypeLabels[type]} ({groupedOutputs[type].length})</h2>
              {groupedOutputs[type].map(output => {
                const campaign = output.campaignId ? campaignMap.get(output.campaignId) : null;
                return (
                  <Card key={output.id} className="cursor-pointer premium-card" onClick={() => setSelectedOutput(output)} data-testid={`card-output-${output.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="space-y-1 min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-sm">{output.title}</h3>
                            <Badge variant={output.status === "approved" ? "default" : "secondary"} className="text-xs capitalize">{output.status}</Badge>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            {campaign && (
                              <Badge variant="secondary" className="text-[10px]">{campaign.name}</Badge>
                            )}
                            {output.funnelLayer && (
                              <Badge variant="secondary" className="text-[10px]">{funnelLayerLabels[output.funnelLayer] || output.funnelLayer}</Badge>
                            )}
                            {output.persuasionStage && (
                              <Badge variant="secondary" className="text-[10px] capitalize">{output.persuasionStage}</Badge>
                            )}
                            {output.assetType && (
                              <Badge variant="secondary" className="text-[10px]">{output.assetType}</Badge>
                            )}
                          </div>
                          {output.contentMarkdown && (
                            <p className="text-xs text-muted-foreground line-clamp-2">{output.contentMarkdown.slice(0, 200)}</p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Sparkles className="h-10 w-10 text-muted-foreground mb-3" />
            <h3 className="font-medium mb-1">No creative outputs yet</h3>
            <p className="text-sm text-muted-foreground">Generate outputs from campaigns or strategy nodes</p>
          </CardContent>
        </Card>
      )}

      {selectedOutput && (
        <Dialog open={!!selectedOutput} onOpenChange={() => setSelectedOutput(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedOutput.title}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex gap-2 flex-wrap">
                <Badge variant="secondary" className="text-xs capitalize">{selectedOutput.outputType.replace(/_/g, " ")}</Badge>
                <Badge variant={selectedOutput.status === "approved" ? "default" : "secondary"} className="text-xs capitalize">{selectedOutput.status}</Badge>
                {selectedOutput.campaignId && campaignMap.get(selectedOutput.campaignId) && (
                  <Badge variant="secondary" className="text-xs">{campaignMap.get(selectedOutput.campaignId)!.name}</Badge>
                )}
                {selectedOutput.funnelLayer && (
                  <Badge variant="secondary" className="text-xs">{funnelLayerLabels[selectedOutput.funnelLayer] || selectedOutput.funnelLayer}</Badge>
                )}
                {selectedOutput.persuasionStage && (
                  <Badge variant="secondary" className="text-xs capitalize">{selectedOutput.persuasionStage}</Badge>
                )}
                {selectedOutput.assetType && (
                  <Badge variant="secondary" className="text-xs">{selectedOutput.assetType}</Badge>
                )}
              </div>
              {selectedOutput.contentMarkdown && (
                <div className="prose prose-sm max-w-none text-muted-foreground whitespace-pre-line text-sm leading-relaxed">
                  {selectedOutput.contentMarkdown}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
